import type { UseMutationOptions } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { apiRequest, isApiError, useApiMutation } from "@/shared/api";
import type { ApiError } from "@/shared/api";
import { clearStoredAccessToken } from "@/shared/utils/auth";
import { clearMembershipSelection } from "@/shared/utils/membership";
import { clearPhoneVerificationState } from "@/shared/utils/phoneVerification";
import { clearPendingEmailVerification } from "../utils/email-verification";

const logoutRequest = () => ({
  url: "/auth/logout",
  method: "post" as const,
});

export const logout = async () => {
  try {
    await apiRequest<void>(logoutRequest());
  } finally {
    clearStoredAccessToken();
    clearMembershipSelection();
    clearPhoneVerificationState();
    clearPendingEmailVerification();
  }
};

export const useLogout = (
  options?: UseMutationOptions<void, ApiError, void>
) => {
  const queryClient = useQueryClient();
  const cleanup = () => {
    clearStoredAccessToken();
    clearMembershipSelection();
    clearPhoneVerificationState();
    clearPendingEmailVerification();
    queryClient.clear();
  };

  return useApiMutation<void, void>(logoutRequest, {
    ...options,
    onSuccess: (data, variables, context, mutation) => {
      cleanup();
      options?.onSuccess?.(data, variables, context, mutation);
    },
    onError: (error, variables, context, mutation) => {
      if (!isApiError(error)) {
        console.error("[auth] Unexpected logout error", error);
      }
      cleanup();
      options?.onError?.(error, variables, context, mutation);
    },
    onSettled: (data, error, variables, context, mutation) => {
      options?.onSettled?.(data, error, variables, context, mutation);
    },
  });
};
